import { useEffect, useRef } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { TENANT_ID } from '@/lib/supabase'

/** Only fixed categories leave the browser. Never inspect an error's contents. */
export default function ErrorMonitor() {
  const { session, isLoading, isAdmin, isSalesperson, b2bCustomer } = useAuth()
  const access = useRef({ session, enabled: false })
  access.current = { session, enabled: !isLoading && (isAdmin || isSalesperson || b2bCustomer?.status === 'approved') }
  useEffect(() => {
    if (!import.meta.env.PROD) return
    try { if (localStorage.getItem('b2b_disable_telemetry') === '1') return } catch { return }
    const last = new Map<string, number>()
    let sent = 0
    const report = (code: 'render' | 'unhandled_error' | 'unhandled_rejection') => {
      if (!access.current.enabled || !access.current.session || sent >= 20) return
      const time = Date.now()
      if (time - (last.get(code) || 0) < 1000) return
      last.set(code, time); sent += 1
      const firstScreen = location.pathname.split('/')[1]
      const screen = ['login', 'catalogo', 'carrinho', 'checkout', 'pedidos', 'perfil', 'admin', 'vendedor', 'proposta'].includes(firstScreen) ? firstScreen : 'outro'
      try {
        void fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/b2b_record_client_error`, {
          method: 'POST', keepalive: true,
          headers: { 'Content-Type': 'application/json', apikey: import.meta.env.VITE_SUPABASE_ANON_KEY, Authorization: `Bearer ${access.current.session.access_token}` },
          body: JSON.stringify({ p_tenant_id: TENANT_ID, p_payload: { event_id: crypto.randomUUID(), code, screen, device: innerWidth < 1024 ? 'mobile' : 'desktop' } }),
        }).catch(() => { /* Reporting cannot disrupt or recursively report a failed purchase. */ })
      } catch { /* The original error handler remains in control. */ }
    }
    const onError = (event: ErrorEvent) => { if (event instanceof ErrorEvent) report('unhandled_error') }
    const onRejection = () => report('unhandled_rejection')
    const onRender = () => report('render')
    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onRejection)
    window.addEventListener('b2b-render-error', onRender)
    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onRejection)
      window.removeEventListener('b2b-render-error', onRender)
    }
  }, [])
  return null
}
